import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtSize, openProtectedFile } from "@/lib/files";
import { Button } from "@/components/ui/button";
import LeadDocumentsField, { DocIcon } from "@/components/leads/LeadDocumentsField";
import ShareLeadDialog from "@/components/leads/ShareLeadDialog";
import { ExternalLink, Paperclip, Share2 } from "lucide-react";

export const SHARE_ROLES = ["admin", "office", "bank", "manager"];
export const MANAGE_DOC_ROLES = ["admin", "office", "bank"];

/* Property papers attached to a lead: view/download for everyone who can see the lead, add/remove + share for allowed roles. */
export default function LeadDocumentsSection({ lead, role, onLeadChanged }) {
  const [share, setShare] = useState(false);
  const [adding, setAdding] = useState(false);
  const docs = lead.documents || [];
  const canManage = MANAGE_DOC_ROLES.includes(role);
  const canShare = SHARE_ROLES.includes(role) && docs.length > 0;

  const open = async (id) => { try { await openProtectedFile(id); } catch (err) { toast.error(formatApiError(err)); } };
  const add = async (doc) => {
    const r = await api.post(`/leads/${lead.id}/documents`, { document_ids: [doc.file_id] });
    onLeadChanged({ documents: r.data.documents, document_ids: r.data.document_ids });
  };
  const remove = async (fileId) => {
    try {
      const r = await api.delete(`/leads/${lead.id}/documents/${fileId}`);
      onLeadChanged({ documents: r.data.documents, document_ids: r.data.document_ids });
      toast.success("Document removed");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="mt-6 border-t border-slate-200 pt-4" data-testid="lead-documents-section">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> Property documents · {docs.length}</div>
        <div className="flex gap-2">
          {canManage && <Button type="button" size="sm" variant="outline" onClick={() => setAdding((v) => !v)} data-testid="lead-docs-add-toggle">{adding ? "Done" : "Add documents"}</Button>}
          {canShare && <Button type="button" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setShare(true)} data-testid="lead-share-button"><Share2 className="h-3.5 w-3.5 mr-1" /> Share</Button>}
        </div>
      </div>
      {adding && canManage ? (
        <LeadDocumentsField docs={docs} onAdd={add} onRemove={remove} label="Attach more documents" testId="lead-detail-docs" />
      ) : docs.length === 0 ? (
        <div className="text-sm text-slate-500" data-testid="lead-docs-empty">No property documents attached.</div>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white" data-testid="lead-docs-list">
          {docs.map((d) => (
            <li key={d.file_id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`lead-doc-${d.file_id}`}>
              <DocIcon kind={d.kind} />
              <span className="truncate flex-1">{d.name}</span>
              <span className="text-[11px] text-slate-400 num">{fmtSize(d.size)}</span>
              <button type="button" onClick={() => open(d.file_id)} className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-950 underline underline-offset-2" data-testid={`lead-doc-open-${d.file_id}`}><ExternalLink className="h-3.5 w-3.5" /> Open</button>
            </li>
          ))}
        </ul>
      )}
      {share && <ShareLeadDialog lead={lead} open={share} onClose={() => setShare(false)} />}
    </div>
  );
}
